// CryptoDeepDive.js - Crypto Dashboard using CACHED data from /top endpoint
// This avoids CoinGecko rate limits by reusing data already fetched
import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  RefreshCw,
  Loader,
  Search,
  Globe,
  BarChart3,
  Activity,
  Zap,
  ChevronDown,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Github,
  Twitter,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import PageAwareAI from './PageAwareAI';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const CryptoDeepDive = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [topCryptos, setTopCryptos] = useState([]);
  const [globalData, setGlobalData] = useState(null);
  const [fearGreed, setFearGreed] = useState(null);
  const [trending, setTrending] = useState([]);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [topRes, globalRes, fearRes, trendRes] = await Promise.all([
        fetch(`${API_BASE}/crypto/top?limit=20`),
        fetch(`${API_BASE}/crypto/global`),
        fetch(`${API_BASE}/crypto/fear-greed`),
        fetch(`${API_BASE}/crypto/trending`)
      ]);

      if (topRes.ok) {
        const topData = await topRes.json();
        setTopCryptos(topData);
      }
      if (globalRes.ok) {
        const globalData = await globalRes.json();
        setGlobalData(globalData);
      }
      if (fearRes.ok) {
        const fearData = await fearRes.json();
        setFearGreed(fearData);
      }
      if (trendRes.ok) {
        const trendData = await trendRes.json();
        setTrending(trendData.coins || []);
      }
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      setError('Failed to load cryptocurrency data');
    } finally {
      setLoading(false);
    }
  };

  // Get selected crypto data from already-fetched topCryptos (NO EXTRA API CALL!)
  const getSelectedCryptoData = () => {
    if (!selectedCrypto) return null;
    return topCryptos.find(c => c.id === selectedCrypto);
  };

  const handleCoinSelect = (coinId) => {
    console.log('Selecting coin:', coinId);
    setSelectedCrypto(coinId);
  };

  const handleCoinSelectAndNavigate = (coinId) => {
    setSelectedCrypto(coinId);
    setActiveTab('detail');
  };

  const formatCurrency = (value, decimals = 2) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(decimals)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(decimals)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(decimals)}M`;
    if (value >= 1000) return `$${value.toLocaleString(undefined, { maximumFractionDigits: decimals })}`;
    if (value < 0.01) return `$${value.toFixed(6)}`;
    return `$${value.toFixed(decimals)}`;
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    const formatted = value.toFixed(2);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    return value.toLocaleString();
  };

  const getFearGreedColor = (value) => {
    if (value <= 25) return '#ef4444';
    if (value <= 45) return '#f97316';
    if (value <= 55) return '#eab308';
    if (value <= 75) return '#84cc16';
    return '#22c55e';
  };

  const filteredCryptos = topCryptos.filter(crypto =>
    crypto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    crypto.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sparkline mini chart
  const SparklineChart = ({ data, color = '#3b82f6', width = 120, height = 40 }) => {
    if (!data || data.length === 0) return <span style={{ color: '#64748b' }}>-</span>;
    
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    
    const points = data.map((value, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      </svg>
    );
  };

  // Fear & Greed Gauge
  const FearGreedGauge = ({ value, size = 180 }) => {
    const angle = (value / 100) * 180 - 90;
    const color = getFearGreedColor(value);
    
    return (
      <div style={{ position: 'relative', width: size, height: size / 2 + 30 }}>
        <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
          <path d={`M 10 ${size / 2} A ${size / 2 - 10} ${size / 2 - 10} 0 0 1 ${size - 10} ${size / 2}`}
            fill="none" stroke="#334155" strokeWidth="20" strokeLinecap="round" />
          <path d={`M 10 ${size / 2} A ${size / 2 - 10} ${size / 2 - 10} 0 0 1 ${size * 0.25} ${size * 0.15}`}
            fill="none" stroke="#ef4444" strokeWidth="20" strokeLinecap="round" />
          <path d={`M ${size * 0.3} ${size * 0.1} A ${size / 2 - 10} ${size / 2 - 10} 0 0 1 ${size * 0.5} ${10}`}
            fill="none" stroke="#f97316" strokeWidth="20" />
          <path d={`M ${size * 0.5} ${10} A ${size / 2 - 10} ${size / 2 - 10} 0 0 1 ${size * 0.7} ${size * 0.1}`}
            fill="none" stroke="#84cc16" strokeWidth="20" />
          <path d={`M ${size * 0.75} ${size * 0.15} A ${size / 2 - 10} ${size / 2 - 10} 0 0 1 ${size - 10} ${size / 2}`}
            fill="none" stroke="#22c55e" strokeWidth="20" strokeLinecap="round" />
        </svg>
        <div style={{
          position: 'absolute', bottom: 30, left: '50%', width: '4px', height: size / 2 - 30,
          background: color, transformOrigin: 'bottom center',
          transform: `translateX(-50%) rotate(${angle}deg)`, borderRadius: '2px'
        }} />
        <div style={{
          position: 'absolute', bottom: 22, left: '50%', transform: 'translateX(-50%)',
          width: 20, height: 20, borderRadius: '50%', background: '#1e293b', border: `3px solid ${color}`
        }} />
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color }}>{value}</div>
        </div>
      </div>
    );
  };

  const selectedData = getSelectedCryptoData();

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
        borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem',
        position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🪙</span>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Crypto Deep Dive</h1>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '0.9rem' }}>
                Real-time cryptocurrency analysis and market data
              </p>
            </div>
            <button onClick={fetchOverviewData} disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem',
                background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '0.5rem', color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem', fontWeight: '600'
              }}>
              {loading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem',
        background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '0.75rem', marginBottom: '1.5rem'
      }}>
        <AlertTriangle size={20} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: '#fbbf24', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
          <strong>High Risk Asset:</strong> Cryptocurrency investments are highly volatile and speculative. 
          This data is for educational purposes only. Never invest more than you can afford to lose.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
        {[
          { id: 'overview', label: 'Overview', icon: Globe },
          { id: 'markets', label: 'Top 20', icon: BarChart3 },
          { id: 'detail', label: 'Deep Dive', icon: Search }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem',
              background: activeTab === tab.id ? 'rgba(245, 158, 11, 0.2)' : 'rgba(30, 41, 59, 0.5)',
              border: activeTab === tab.id ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid #334155',
              borderRadius: '0.5rem', color: activeTab === tab.id ? '#fbbf24' : '#94a3b8',
              cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', whiteSpace: 'nowrap'
            }}>
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader size={48} color="#f59e0b" className="spin" />
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.75rem', textAlign: 'center' }}>
          <p style={{ color: '#fca5a5' }}>{error}</p>
          <button onClick={fetchOverviewData}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#3b82f6', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              {/* Global Stats */}
              {globalData && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Globe size={18} color="#f59e0b" />
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Total Market Cap</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(globalData.totalMarketCap)}</div>
                    <div style={{ fontSize: '0.85rem', color: globalData.marketCapChange24h >= 0 ? '#22c55e' : '#ef4444', marginTop: '0.25rem' }}>
                      {formatPercent(globalData.marketCapChange24h)} (24h)
                    </div>
                  </div>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Activity size={18} color="#3b82f6" />
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>24h Volume</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(globalData.totalVolume24h)}</div>
                  </div>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>₿</span>
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>BTC Dominance</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{globalData.btcDominance?.toFixed(1)}%</div>
                  </div>
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.25rem', border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>Ξ</span>
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>ETH Dominance</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{globalData.ethDominance?.toFixed(1)}%</div>
                  </div>
                </div>
              )}

              {/* Fear & Greed + Trending */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                {fearGreed && (
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Zap size={20} color="#f59e0b" /> Fear & Greed Index
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <FearGreedGauge value={fearGreed.current?.value || 50} />
                      <div style={{
                        marginTop: '0.5rem', padding: '0.375rem 1rem',
                        background: `${getFearGreedColor(fearGreed.current?.value)}20`,
                        borderRadius: '2rem', color: getFearGreedColor(fearGreed.current?.value),
                        fontSize: '0.875rem', fontWeight: '600'
                      }}>
                        {fearGreed.current?.classification || 'Neutral'}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={20} color="#22c55e" /> Trending Now
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {trending.slice(0, 7).map((coin, idx) => (
                      <div key={coin.id} onClick={() => handleCoinSelectAndNavigate(coin.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(51, 65, 85, 0.5)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ color: '#64748b', fontSize: '0.8rem', width: '20px' }}>#{idx + 1}</span>
                        <img src={coin.thumb} alt={coin.name} style={{ width: 24, height: 24, borderRadius: '50%' }} />
                        <span style={{ fontWeight: '500', flex: 1 }}>{coin.name}</span>
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{coin.symbol}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top 5 Preview */}
              <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Star size={20} color="#f59e0b" /> Top Cryptocurrencies
                  </h3>
                  <button onClick={() => setActiveTab('markets')}
                    style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    View All <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.8rem' }}>#</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.8rem' }}>Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>Price</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>24h %</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>7d %</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>Market Cap</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>7d Chart</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCryptos.slice(0, 5).map((crypto) => (
                        <tr key={crypto.id} onClick={() => handleCoinSelectAndNavigate(crypto.id)}
                          style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(51, 65, 85, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '0.75rem', color: '#64748b' }}>{crypto.marketCapRank}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <img src={crypto.image} alt={crypto.name} style={{ width: 28, height: 28, borderRadius: '50%' }} />
                              <div>
                                <div style={{ fontWeight: '600' }}>{crypto.name}</div>
                                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{crypto.symbol}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                            {formatCurrency(crypto.currentPrice, crypto.currentPrice < 1 ? 4 : 2)}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: crypto.priceChange24h >= 0 ? '#22c55e' : '#ef4444' }}>
                            {formatPercent(crypto.priceChange24h)}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: crypto.priceChange7d >= 0 ? '#22c55e' : '#ef4444' }}>
                            {formatPercent(crypto.priceChange7d)}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(crypto.marketCap)}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <SparklineChart data={crypto.sparkline} color={crypto.priceChange7d >= 0 ? '#22c55e' : '#ef4444'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Markets Tab */}
          {activeTab === 'markets' && (
            <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155' }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem', border: '1px solid #334155' }}>
                  <Search size={18} color="#64748b" />
                  <input type="text" placeholder="Search cryptocurrencies..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ flex: 1, background: 'none', border: 'none', color: 'white', fontSize: '0.9rem', outline: 'none' }} />
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.8rem' }}>#</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.8rem' }}>Name</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>Price</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>1h %</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>24h %</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>7d %</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>Market Cap</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.8rem' }}>Volume (24h)</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>7d Chart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCryptos.map((crypto) => (
                      <tr key={crypto.id} onClick={() => handleCoinSelectAndNavigate(crypto.id)}
                        style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(51, 65, 85, 0.3)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '0.75rem', color: '#64748b' }}>{crypto.marketCapRank}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <img src={crypto.image} alt={crypto.name} style={{ width: 28, height: 28, borderRadius: '50%' }} />
                            <div>
                              <div style={{ fontWeight: '600' }}>{crypto.name}</div>
                              <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{crypto.symbol}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                          {formatCurrency(crypto.currentPrice, crypto.currentPrice < 1 ? 4 : 2)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: (crypto.priceChange1h || 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                          {formatPercent(crypto.priceChange1h)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: crypto.priceChange24h >= 0 ? '#22c55e' : '#ef4444' }}>
                          {formatPercent(crypto.priceChange24h)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: crypto.priceChange7d >= 0 ? '#22c55e' : '#ef4444' }}>
                          {formatPercent(crypto.priceChange7d)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(crypto.marketCap)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(crypto.volume24h)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <SparklineChart data={crypto.sparkline} color={crypto.priceChange7d >= 0 ? '#22c55e' : '#ef4444'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detail Tab - Uses data from topCryptos (NO EXTRA API CALLS!) */}
          {activeTab === 'detail' && (
            <div>
              {/* Coin Selector */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                {topCryptos.slice(0, 10).map(crypto => (
                  <button key={crypto.id} onClick={() => handleCoinSelect(crypto.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                      background: selectedCrypto === crypto.id ? 'rgba(245, 158, 11, 0.2)' : 'rgba(30, 41, 59, 0.5)',
                      border: selectedCrypto === crypto.id ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid #334155',
                      borderRadius: '2rem', color: selectedCrypto === crypto.id ? '#fbbf24' : '#94a3b8',
                      cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', flexShrink: 0
                    }}>
                    <img src={crypto.image} alt={crypto.name} style={{ width: 20, height: 20, borderRadius: '50%' }} />
                    {crypto.symbol}
                  </button>
                ))}
              </div>

              {!selectedCrypto || !selectedData ? (
                <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', border: '1px solid #334155' }}>
                  <Search size={48} color="#64748b" style={{ marginBottom: '1rem' }} />
                  <h3 style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>Select a Cryptocurrency</h3>
                  <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Click on any coin above to see detailed analysis</p>
                </div>
              ) : (
                <div>
                  {/* Coin Header */}
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                      <img src={selectedData.image} alt={selectedData.name} style={{ width: 64, height: 64, borderRadius: '50%' }} />
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{selectedData.name}</h2>
                          <span style={{ padding: '0.25rem 0.5rem', background: 'rgba(148, 163, 184, 0.2)', borderRadius: '0.25rem', color: '#94a3b8', fontSize: '0.8rem' }}>
                            {selectedData.symbol}
                          </span>
                          <span style={{ padding: '0.25rem 0.5rem', background: 'rgba(245, 158, 11, 0.2)', borderRadius: '0.25rem', color: '#f59e0b', fontSize: '0.8rem' }}>
                            Rank #{selectedData.marketCapRank}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                            {formatCurrency(selectedData.currentPrice, selectedData.currentPrice < 1 ? 6 : 2)}
                          </span>
                          <span style={{ fontSize: '1.25rem', color: selectedData.priceChange24h >= 0 ? '#22c55e' : '#ef4444', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {selectedData.priceChange24h >= 0 ? <ArrowUpRight size={20} /> : <ArrowDownRight size={20} />}
                            {formatPercent(selectedData.priceChange24h)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Price Stats Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Market Cap</div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>{formatCurrency(selectedData.marketCap)}</div>
                    </div>
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>24h Volume</div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>{formatCurrency(selectedData.volume24h)}</div>
                    </div>
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>24h High</div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#22c55e' }}>{formatCurrency(selectedData.high24h)}</div>
                    </div>
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>24h Low</div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ef4444' }}>{formatCurrency(selectedData.low24h)}</div>
                    </div>
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>All-Time High</div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>{formatCurrency(selectedData.ath)}</div>
                      <div style={{ fontSize: '0.75rem', color: '#ef4444' }}>{formatPercent(selectedData.athChangePercentage)}</div>
                    </div>
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Circulating Supply</div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>{formatNumber(selectedData.circulatingSupply?.toFixed(0))}</div>
                      {selectedData.maxSupply && (
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Max: {formatNumber(selectedData.maxSupply?.toFixed(0))}</div>
                      )}
                    </div>
                  </div>

                  {/* Price Performance */}
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Price Performance</h3>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {[
                        { label: '1h', value: selectedData.priceChange1h },
                        { label: '24h', value: selectedData.priceChange24h },
                        { label: '7d', value: selectedData.priceChange7d },
                        { label: '30d', value: selectedData.priceChange30d }
                      ].map(item => (
                        <div key={item.label} style={{ flex: '1 1 100px', padding: '1rem', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '0.5rem', textAlign: 'center' }}>
                          <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.25rem' }}>{item.label}</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '600', color: (item.value || 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                            {formatPercent(item.value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 7d Sparkline Chart */}
                  {selectedData.sparkline && selectedData.sparkline.length > 0 && (
                    <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155' }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>7-Day Price Chart</h3>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <SparklineChart 
                          data={selectedData.sparkline} 
                          color={selectedData.priceChange7d >= 0 ? '#22c55e' : '#ef4444'} 
                          width={600} 
                          height={200} 
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <PageAwareAI 
  pageId="crypto"
  pageData={{
    coin: selectedCrypto,
    topCryptos: topCryptos,
    globalData: globalData,
    fearGreed: fearGreed,
    trending: trending,
    activeTab: activeTab
  }}
  symbol={selectedCrypto?.symbol}
/>
    </div>
  );
};

export default CryptoDeepDive;
